import { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { vehicleOpsApi } from "@/services/vehicleOps";
import { listVehicles } from "@/services/vehicles";
import { ScannerCapture } from "@/shared/ui/ScannerCapture";
import { uploadReceipt } from "@/services/uploads";
import { AuthedImage } from "@/shared/ui/AuthedImage";

const CATS = [
  { value: "fuel", icon: "⛽", label: "油费", sub: "加油 / 充电" },
  { value: "vehicle_maintenance", icon: "🔧", label: "维修", sub: "需先建维修工单" },
  { value: "insurance", icon: "🛡", label: "保险", sub: "商业保险 / COI" },
  { value: "fine", icon: "💳", label: "罚款", sub: "超速 / 停车 / 路费" },
];
const PAY = ["公司信用卡", "现金", "公司转账", "司导垫付", "其他"];
const inp = "w-full rounded-lg border border-slate-300 px-3 py-2 text-[14px]";

export function VehicleOpsExpenseForm() {
  const nav = useNavigate();
  const vehQ = useQuery({ queryKey: ["vehicles", "all"], queryFn: () => listVehicles() });
  const [f, setF] = useState({
    vehicleId: "", category: "fuel", maintId: "", date: new Date().toISOString().slice(0, 10),
    vendor: "", amount: "", currency: "USD", tax: "", payment: "公司信用卡", notes: "",
  });
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));
  // 维修类：关联维修工单（按所选车筛）
  const maintQ = useQuery({
    queryKey: ["maint", f.vehicleId],
    queryFn: () => vehicleOpsApi.listMaintenance({ vehicle_id: f.vehicleId }),
    enabled: f.category === "vehicle_maintenance" && !!f.vehicleId,
  });
  const [scannerOpen, setScannerOpen] = useState(false);
  const [receiptUrl, setReceiptUrl] = useState("");
  const [lightbox, setLightbox] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const back = () => nav("/admin/vehicle-operations/expenses");

  async function submit() {
    setError(null);
    const cents = Math.round(Number(f.amount) * 100);
    if (!cents || cents < 0) { setError("金额无效"); return; }
    if (f.category === "vehicle_maintenance" && !f.maintId) { setError("维修类费用必须关联维修工单（先选车 → 选工单；无工单请先建）"); return; }
    const desc = [
      f.vendor ? `供应方: ${f.vendor}` : "", f.tax ? `税额: $${f.tax}` : "",
      `付款: ${f.payment}`, f.notes,
    ].filter(Boolean).join(" · ");
    setSubmitting(true);
    try {
      await vehicleOpsApi.uploadExpense({
        category: f.category, amount_cents: cents, receipt_date: f.date,
        related_vehicle_id: f.vehicleId || undefined,
        related_maintenance_order_id: f.category === "vehicle_maintenance" ? f.maintId || undefined : undefined,
        description: desc || undefined,
        receipt_image_url: receiptUrl || undefined,
      });
      back();
    } catch (e: unknown) { setError((e as Error).message); } finally { setSubmitting(false); }
  }

  return createPortal(
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-50">
      <header className="flex items-center justify-between border-b border-slate-200 bg-white px-6 py-3.5">
        <div><h1 className="text-[17px] font-bold text-slate-900">💸 上传费用收据</h1><p className="text-[12px] text-slate-500">4 类 · 归车务台账（origin=vehicle_ops）· 与财务应付账款 mirror</p></div>
        <button onClick={back} className="rounded-lg border border-slate-300 px-3.5 py-2 text-[13px] font-medium hover:bg-slate-50">✕ 取消</button>
      </header>

      <div className="flex-1 overflow-y-auto px-8 py-6">
        <div className="flex flex-col gap-5">
          {/* 上传凭证文件（扫描仪模式）*/}
          <Section title="📁 上传凭证（扫描仪模式）">
            {receiptUrl ? (
              <div className="flex items-center gap-3">
                <div className="relative h-24 w-24 shrink-0">
                  <button type="button" onClick={() => setLightbox(true)} className="group h-full w-full overflow-hidden rounded-lg border border-slate-200" title="点击放大查看">
                    <AuthedImage src={receiptUrl} alt="凭证" className="h-full w-full object-cover" />
                    <span className="absolute inset-0 hidden items-center justify-center bg-black/40 text-[11px] font-semibold text-white group-hover:flex">🔍 放大</span>
                  </button>
                  <button type="button" onClick={() => setReceiptUrl("")} title="删除"
                    className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-rose-500 text-sm font-bold leading-none text-white shadow hover:bg-rose-600">×</button>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-[13px] font-semibold text-emerald-700">✓ 凭证已上传（拉正+黑白扫描件）</span>
                  <button onClick={() => setLightbox(true)} className="text-left text-[12px] text-blue-600 hover:underline">🔍 放大查看</button>
                  <button onClick={() => setScannerOpen(true)} className="text-left text-[12px] text-blue-600 hover:underline">重新扫描</button>
                  <button onClick={() => setReceiptUrl("")} className="text-left text-[12px] text-rose-600 hover:underline">✕ 删除</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setScannerOpen(true)} className="flex w-full cursor-pointer flex-col items-center gap-1 rounded-2xl border-2 border-dashed border-slate-300 py-8 hover:bg-slate-50">
                <span className="text-3xl">📷</span>
                <span className="text-[14px] font-semibold text-blue-600">扫描凭证</span>
                <span className="text-[12px] text-slate-500">自动检测边缘 · 拉正裁切 · 增强清晰</span>
              </button>
            )}
          </Section>

          {/* OCR 识别结果（自动填充·接入中→手填）*/}
          <Section title="🤖 字段（OCR 自动填充接入中 · 暂手填）">
            <div>
              <Lbl>类别 *</Lbl>
              <div className="grid grid-cols-4 gap-2">
                {CATS.map((c) => (
                  <button key={c.value} onClick={() => set("category", c.value)} className={`rounded-lg border-2 px-3 py-2.5 text-left ${f.category === c.value ? "border-blue-600 bg-blue-50" : "border-slate-200"}`}>
                    <div className="text-[14px] font-semibold">{c.icon} {c.label}</div>
                    <div className="text-[10px] text-slate-500">{c.sub}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="车辆 *"><select value={f.vehicleId} onChange={(e) => set("vehicleId", e.target.value)} className={inp}><option value="">（不关联具体车）</option>{(vehQ.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.vehicle_number} · {v.model_label_cn}</option>)}</select></Field>
              <Field label="日期 *"><input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} className={inp} /></Field>
            </div>
            {f.category === "vehicle_maintenance" && (
              <Field label="🔧 关联维修工单 *（维修费用必须挂到工单·财务对账）">
                {!f.vehicleId ? (
                  <div className="rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-700">请先选车辆 ↑</div>
                ) : (maintQ.data ?? []).length === 0 ? (
                  <div className="rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-700">该车暂无维修工单 · <Link to="/admin/vehicle-operations/maintenance/new" className="font-semibold text-blue-600 underline">先建维修工单 →</Link></div>
                ) : (
                  <select value={f.maintId} onChange={(e) => set("maintId", e.target.value)} className={inp}>
                    <option value="">选择维修工单…</option>
                    {(maintQ.data ?? []).map((m) => <option key={m.id} value={m.id}>{m.order_number} · {(m.description ?? "").slice(0, 18)} · {m.status}</option>)}
                  </select>
                )}
              </Field>
            )}
            <Field label="供应方"><input value={f.vendor} onChange={(e) => set("vendor", e.target.value)} placeholder="如 Shell Yellowstone Station" className={inp} /></Field>
            <div className="grid grid-cols-3 gap-4">
              <Field label="金额 *"><input value={f.amount} onChange={(e) => set("amount", e.target.value)} inputMode="decimal" placeholder="0.00" className={inp} /></Field>
              <Field label="币种 *"><input value={f.currency} onChange={(e) => set("currency", e.target.value)} className={inp} /></Field>
              <Field label="税额（如有）"><input value={f.tax} onChange={(e) => set("tax", e.target.value)} inputMode="decimal" placeholder="0.00" className={inp} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="付款方式"><select value={f.payment} onChange={(e) => set("payment", e.target.value)} className={inp}>{PAY.map((p) => <option key={p}>{p}</option>)}</select></Field>
              <Field label="备注"><input value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="如 加油 32.4 加仑" className={inp} /></Field>
            </div>
          </Section>

          {/* QB 推送（mock）*/}
          <Section title="🔗 QB Expense 推送">
            <div className="rounded-lg bg-slate-50 p-3 text-[12px] text-slate-500">
              ✅ 同步规则：自动归类 QB Expense Category · 关联 QB Class · 附件凭证 · <span className="font-semibold text-slate-700">待接财务模块（QuickBooks）</span>
            </div>
          </Section>

          {error && <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[13px] text-rose-700">{error}</div>}
        </div>
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-8 py-3.5">
        <button onClick={back} className="rounded-lg border border-slate-300 px-4 py-2 text-[13px] font-medium hover:bg-slate-50">取消</button>
        <button onClick={submit} disabled={submitting} className="rounded-lg bg-blue-600 px-5 py-2 text-[13px] font-semibold text-white hover:bg-blue-700">{submitting ? "保存中…" : "保存（推 QB 待财务模块）"}</button>
      </footer>

      <ScannerCapture
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        upload={uploadReceipt}
        onUploaded={(url) => { setReceiptUrl(url); setScannerOpen(false); }}
      />

      {lightbox && receiptUrl && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-4" onClick={() => setLightbox(false)}>
          <AuthedImage src={receiptUrl} alt="凭证放大" className="max-h-[92vh] max-w-[92vw] rounded-lg bg-white object-contain shadow-2xl" onClick={(e) => e.stopPropagation()} />
          <button onClick={() => setLightbox(false)} className="absolute right-5 top-5 text-3xl leading-none text-white/80 hover:text-white">×</button>
        </div>
      )}
    </div>,
    document.body,
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6">
      <div className="mb-3 text-[14px] font-semibold text-slate-900">{title}</div>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="flex flex-col gap-1.5"><Lbl>{label}</Lbl>{children}</label>;
}
function Lbl({ children }: { children: React.ReactNode }) {
  return <span className="text-[13px] font-medium text-slate-700">{children}</span>;
}
